/**
 * Shared chrome for Pacarion notification emails.
 * The wordmark is white on a navy band, and the PNG itself has that navy
 * baked in, so Gmail dark mode cannot turn a transparent navy wordmark
 * into invisible text. Auth templates under supabase/templates use the
 * same image and band.
 */

const {
  PACARION_POSTAL_ADDRESS,
  PACARION_SUPPORT_EMAIL,
} = require('./sponsorlessBrand');
const { formatPhoneDisplay, phoneTelHref } = require('./phoneFormat');

const PACARION_LOGO_URL = 'https://app.pacarion.com/images/logo/pacarion-logo-email.png';
const PACARION_EMAIL_HEADER_BG = '#0f172a';
const PACARION_LOGO_WIDTH = 220;
const PACARION_LOGO_HEIGHT = 51;

const LOGO_MAX_WIDTH = 180;
const LOGO_MAX_HEIGHT = 60;
const LOGO_MAX_NATURAL = 20000;

function positiveLogoPixel(value) {
  const number = Number(value);
  if (!Number.isFinite(number) || number <= 0) return 0;
  const rounded = Math.round(number);
  if (rounded <= 0 || rounded > LOGO_MAX_NATURAL) return 0;
  return rounded;
}

/**
 * Pixel width and height for an email <img>.
 * Unknown input returns height only, so clients scale the width from the file.
 * The rendered size never exceeds the image's natural size.
 */
function fitLogoDimensions(naturalWidth, naturalHeight) {
  const width = positiveLogoPixel(naturalWidth);
  const height = positiveLogoPixel(naturalHeight);
  if (!width || !height) return { width: null, height: LOGO_MAX_HEIGHT };
  const scale = Math.min(LOGO_MAX_WIDTH / width, LOGO_MAX_HEIGHT / height, 1);
  let renderedWidth = Math.max(1, Math.round(width * scale));
  let renderedHeight = Math.max(1, Math.round(height * scale));
  renderedWidth = Math.min(renderedWidth, width, LOGO_MAX_WIDTH);
  renderedHeight = Math.min(renderedHeight, height, LOGO_MAX_HEIGHT);
  return { width: renderedWidth, height: renderedHeight };
}

const SOCIAL_FIELDS = [
  ['instagram', 'Instagram'],
  ['instagram_url', 'Instagram'],
  ['facebook', 'Facebook'],
  ['facebook_url', 'Facebook'],
  ['linkedin', 'LinkedIn'],
  ['linkedin_url', 'LinkedIn'],
  ['twitter', 'X'],
  ['twitter_url', 'X'],
  ['tiktok', 'TikTok'],
  ['tiktok_url', 'TikTok'],
  ['social', 'Social'],
  ['social_url', 'Social'],
];

function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function present(value) {
  const trimmed = String(value == null ? '' : value).trim();
  return trimmed;
}

function agentDisplayName(agent) {
  if (!agent || typeof agent !== 'object') return '';
  return present(`${agent.first_name || ''} ${agent.last_name || ''}`);
}

function httpUrl(value) {
  const raw = present(value);
  if (!/^https?:\/\//i.test(raw)) return '';
  return raw;
}

function webHref(value) {
  const raw = present(value);
  if (!raw) return '';
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^[\w.-]+\.[a-z]{2,}([/?#].*)?$/i.test(raw)) return `https://${raw}`;
  return '';
}

function emailHref(value) {
  const raw = present(value);
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return '';
  return `mailto:${raw}`;
}

function phoneHref(value) {
  const raw = present(value);
  const tel = raw.replace(/[^\d+]/g, '');
  if (!tel) return '';
  return `tel:${tel}`;
}

function safeHex(value) {
  const color = present(value);
  return /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i.test(color) ? color : '#59c4c4';
}

function socialEntries(agent) {
  if (!agent || typeof agent !== 'object') return [];
  const seen = new Set();
  const entries = [];
  SOCIAL_FIELDS.forEach(([key, label]) => {
    const value = present(agent[key]);
    if (!value || seen.has(`${label}:${value}`)) return;
    seen.add(`${label}:${value}`);
    entries.push({ label, value, href: webHref(value) || emailHref(value) });
  });
  const bag = agent.socials || agent.social_links;
  if (bag && typeof bag === 'object' && !Array.isArray(bag)) {
    Object.keys(bag).forEach((key) => {
      const value = present(bag[key]);
      if (!value) return;
      const label = key.replace(/[_-]+/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
      const mark = `${label}:${value}`;
      if (seen.has(mark)) return;
      seen.add(mark);
      entries.push({ label, value, href: webHref(value) || emailHref(value) });
    });
  }
  return entries;
}

function brokerageLogoTag(agent, brokerage) {
  const url = httpUrl(agent && agent.logo_url);
  if (!url) return '';
  const fitted = fitLogoDimensions(agent.logo_width, agent.logo_height);
  const known = fitted.width != null;
  const size = known
    ? `width="${fitted.width}" height="${fitted.height}"`
    : `height="${fitted.height}"`;
  // Outlook uses the width and height attributes. Gmail and Apple Mail
  // honor the CSS and let height:auto override the height attribute, so an
  // unknown tall file also needs max-height and width:auto to stay in 180×60.
  const box = known
    ? `max-width:${LOGO_MAX_WIDTH}px;max-height:${LOGO_MAX_HEIGHT}px;height:auto;`
    : `max-width:${LOGO_MAX_WIDTH}px;max-height:${LOGO_MAX_HEIGHT}px;width:auto;height:auto;`;
  const style = `display:block;border:0;outline:none;text-decoration:none;${box}`;
  return `<img src="${escapeHtml(url)}" alt="${escapeHtml(brokerage || 'Brokerage')}" ${size} style="${style}" />`;
}

function imageTag({ src, alt, width, height }) {
  const url = httpUrl(src);
  if (!url) return '';
  return `<img src="${escapeHtml(url)}" alt="${escapeHtml(alt || '')}" width="${width}" height="${height}" style="display:block;border:0;outline:none;text-decoration:none;width:${width}px;height:${height}px;max-width:100%;" />`;
}

function logoHeaderHtml() {
  return imageTag({
    src: PACARION_LOGO_URL,
    alt: 'Pacarion',
    width: PACARION_LOGO_WIDTH,
    height: PACARION_LOGO_HEIGHT,
  });
}

function textLine(label, value, href) {
  const safe = escapeHtml(value);
  const body = href
    ? `<a href="${escapeHtml(href)}" style="color:#0f766e;text-decoration:none;">${safe}</a>`
    : safe;
  return `<tr><td style="padding:2px 0;font-size:14px;line-height:1.5;color:#333333;"><span style="color:#667085;">${escapeHtml(label)}:</span> ${body}</td></tr>`;
}

function agentBrandingHtml(agent, options) {
  if (!agent || typeof agent !== 'object') return '';
  const personLabel = (options && options.personLabel) || 'Agent';
  const name = agentDisplayName(agent);
  const brokerage = present(agent.brokerage);
  const phoneRaw = present(agent.phone);
  const phone = formatPhoneDisplay(phoneRaw);
  const email = present(agent.email);
  const website = present(agent.website);
  const teamName = present(agent.team_name);
  const headshot = imageTag({
    src: agent.headshot_url,
    alt: name || personLabel,
    width: 72,
    height: 72,
  });
  const brokerageLogo = brokerageLogoTag(agent, brokerage);
  const brokeragePhoto = imageTag({
    src: agent.brokerage_photo_url,
    alt: brokerage || 'Brokerage',
    width: 220,
    height: 120,
  });
  const socials = socialEntries(agent);
  const lines = [];
  if (name) lines.push(textLine(personLabel, name));
  if (brokerage) lines.push(textLine('Brokerage', brokerage));
  if (teamName) lines.push(textLine('Team', teamName));
  if (phone) lines.push(textLine('Phone', phone, phoneTelHref(phoneRaw) || phoneHref(phoneRaw)));
  if (email) lines.push(textLine('Email', email, emailHref(email)));
  if (website) lines.push(textLine('Website', website, webHref(website)));
  socials.forEach((entry) => lines.push(textLine(entry.label, entry.value, entry.href)));
  if (!headshot && !brokerageLogo && !brokeragePhoto && lines.length === 0) return '';

  const images = [headshot, brokerageLogo, brokeragePhoto].filter(Boolean);
  // Inline blocks wrap on a phone. Outlook desktop only sees the fixed cells.
  const imageRow = images.length
    ? '<!--[if mso]><table role="presentation" cellpadding="0" cellspacing="0" border="0"><tr><![endif]-->'
      + images.map((img) => (
        '<!--[if mso]><td valign="middle" style="padding:0 12px 8px 0;"><![endif]-->'
        + `<div style="display:inline-block;vertical-align:middle;max-width:100%;padding:0 12px 8px 0;">${img}</div>`
        + '<!--[if mso]></td><![endif]-->'
      )).join('')
      + '<!--[if mso]></tr></table><![endif]-->'
    : '';
  const accent = safeHex(agent.brand_color);
  return `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;margin-top:8px;border-top:3px solid ${accent};">
    <tr><td style="padding:16px 0 0;">
      ${imageRow}
      ${lines.length ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="width:100%;border-collapse:collapse;">${lines.join('')}</table>` : ''}
    </td></tr>
  </table>`;
}

function pacarionFooterHtml(options) {
  const opts = options || {};
  const year = opts.year || new Date().getFullYear();
  const postal = present(opts.postal) || PACARION_POSTAL_ADDRESS;
  const sentTo = present(opts.sentTo);
  const unsub = httpUrl(opts.unsubscribeUrl);
  const sent = sentTo ? `<br>This email was sent to ${escapeHtml(sentTo)}.` : '';
  const unsubHtml = unsub
    ? `<br><a href="${escapeHtml(unsub)}" style="color:#667085;">Unsubscribe</a>`
    : '';
  return `<p style="margin:0;color:#667085;font-size:12px;line-height:1.6;text-align:center;">Pacarion LLC<br>${escapeHtml(postal)}<br><a href="mailto:${PACARION_SUPPORT_EMAIL}" style="color:#0f766e;text-decoration:none;">${PACARION_SUPPORT_EMAIL}</a>${sent}${unsubHtml}<br>© ${year} Pacarion. All rights reserved.</p>`;
}

function hasAgentBranding(agent) {
  return Boolean(agentBrandingHtml(agent));
}

/**
 * Homeowner mail. A sponsor keeps the agent block and the caller's footer.
 * No sponsor uses the Pacarion footer (address and support) and no agent block.
 */
function footerWithPostal(footerHtml, options) {
  const opts = options || {};
  const provided = String(footerHtml || '');
  if (!provided) {
    return pacarionFooterHtml({
      year: opts.year,
      sentTo: opts.sentTo,
      unsubscribeUrl: opts.unsubscribeUrl,
      postal: opts.postal,
    });
  }
  if (provided.indexOf(PACARION_POSTAL_ADDRESS) !== -1) return provided;
  const postal = present(opts.postal) || PACARION_POSTAL_ADDRESS;
  return `${provided}<p style="margin:8px 0 0;color:#667085;font-size:12px;line-height:1.6;text-align:center;">Pacarion LLC<br>${escapeHtml(postal)}</p>`;
}

function homeownerNotificationShell(options) {
  const opts = options || {};
  const footer = footerWithPostal(opts.footerHtml, opts);
  if (!hasAgentBranding(opts.agent)) {
    return notificationShell({
      title: opts.title,
      bodyHtml: opts.bodyHtml,
      agent: null,
      personLabel: 'Sponsor',
      footerHtml: footer,
    });
  }
  return notificationShell({
    title: opts.title,
    bodyHtml: opts.bodyHtml,
    agent: opts.agent,
    personLabel: 'Sponsor',
    footerHtml: footer,
  });
}

function notificationShell({ title, bodyHtml, agent, footerHtml, personLabel }) {
  const year = new Date().getFullYear();
  const branding = agentBrandingHtml(agent, { personLabel });
  const fallback = branding
    ? `<p style="margin:0;color:#667085;font-size:12px;line-height:1.6;text-align:center;">© ${year} Pacarion. All rights reserved.</p>`
    : pacarionFooterHtml({ year });
  const footer = footerHtml || fallback;
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <meta name="color-scheme" content="light dark">
  <meta name="supported-color-schemes" content="light dark">
  <title>${escapeHtml(title || 'Pacarion')}</title>
  <style>
    .pacarion-email-header { background: ${PACARION_EMAIL_HEADER_BG} !important; background-color: ${PACARION_EMAIL_HEADER_BG} !important; }
  </style>
</head>
<body style="margin:0;padding:0;background-color:#f5f5f5;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%;border-collapse:collapse;background-color:#f5f5f5;">
    <tr>
      <td align="center" style="padding:24px 12px;">
        <!--[if mso]>
        <table role="presentation" align="center" width="600" cellpadding="0" cellspacing="0" border="0"><tr><td>
        <![endif]-->
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" align="center" style="width:100%;max-width:600px;border-collapse:collapse;background-color:#ffffff;border-radius:8px;">
          <tr>
            <td class="pacarion-email-header" align="center" bgcolor="${PACARION_EMAIL_HEADER_BG}" style="padding:22px 20px;background-color:${PACARION_EMAIL_HEADER_BG};">
              ${logoHeaderHtml()}
            </td>
          </tr>
          <tr>
            <td style="padding:8px 20px 24px;color:#333333;font-size:16px;line-height:1.6;overflow-wrap:anywhere;">
              ${bodyHtml || ''}
              ${branding}
            </td>
          </tr>
          <tr>
            <td style="padding:16px 20px 24px;background-color:#f9f9f9;border-top:1px solid #e6eeee;">
              ${footer}
            </td>
          </tr>
        </table>
        <!--[if mso]>
        </td></tr></table>
        <![endif]-->
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/**
 * Homeowner-facing mail uses the assigned team member when one exists.
 * Otherwise it uses the agent on the project, then the client's agent
 * (the solo agent, or the team owner when nobody is assigned).
 */
function personName(record) {
  if (!record || typeof record !== 'object') return '';
  return present(`${record.first_name || ''} ${record.last_name || ''}`);
}

/**
 * Agent completion mail greets the recipient and names the homeowner as
 * the client. A shared first name ("Hi Charlie" / "Charlie has completed")
 * is not used as if they were the same person.
 */
function completionNotice({ client, agent } = {}) {
  const clientFirst = present(client && client.first_name);
  const clientFull = personName(client);
  const agentFirst = present(agent && agent.first_name) || personName(agent) || 'there';
  const agentFull = personName(agent);
  const sameFull = clientFull && agentFull && clientFull.toLowerCase() === agentFull.toLowerCase();
  const sameFirst = clientFirst && agentFirst && clientFirst.toLowerCase() === agentFirst.toLowerCase();
  let completedBy = 'Your client';
  if (clientFull && !sameFull && !(sameFirst && !present(client && client.last_name))) {
    completedBy = `Your client ${clientFull}`;
  }
  return {
    greetingName: agentFirst,
    completedBy,
    subjectName: clientFull || 'Your client',
  };
}

function homeownerBrandingAgentId({ client, projectAgentId } = {}) {
  if (client && client.assigned_agent_id) return client.assigned_agent_id;
  if (projectAgentId) return projectAgentId;
  if (client && client.agent_id) return client.agent_id;
  return null;
}

// Rendered into docs/email-previews and checked by emailLayout.test.js.
// eslint-disable-next-line no-unused-vars
function sampleCompletionEmail() {
  const client = { first_name: 'Charlie', last_name: 'Lee' };
  const agent = {
    first_name: 'Charlie',
    last_name: 'Peterson',
    brokerage: 'The Peterson Group',
    phone: '(407) 555-0100',
    email: 'charlie@thepetersongroup.example',
    website: 'thepetersongroup.example',
  };
  const notice = completionNotice({ client, agent });
  const body = `
    <h2 style="margin:0 0 16px;color:#333333;font-size:24px;font-weight:500;">Project Completed!</h2>
    <p style="margin:0 0 16px;">Hi ${escapeHtml(notice.greetingName)},</p>
    <p style="margin:0 0 16px;">Great news! <strong>${escapeHtml(notice.completedBy)}</strong> has completed all tasks in their project checklist.</p>
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:16px 0;background-color:#59c4c4;border-radius:8px;">
      <tr><td style="padding:20px;">
        <h3 style="margin:0 0 8px;color:#ffffff;font-size:20px;font-weight:500;">Pre-Listing Preparation</h3>
        <p style="margin:0;color:#ffffff;font-size:15px;">10 tasks completed on Thursday, October 15, 2026</p>
      </td></tr>
    </table>
    <p style="margin:16px 0;"><a href="https://app.pacarion.com/?login=agent" style="display:inline-block;padding:12px 24px;background-color:#0f766e;color:#ffffff;text-decoration:none;border-radius:6px;">View Dashboard</a></p>
    <p style="margin:0;color:#666666;font-size:14px;">Need help? Contact Pacarion support at support@pacarion.com.</p>
  `;
  return notificationShell({
    title: 'Client Completed Project',
    bodyHtml: body,
    agent,
    footerHtml: '<p style="margin:0;color:#667085;font-size:12px;line-height:1.6;text-align:center;">© 2026 Pacarion. All rights reserved.<br>This email was sent to charlie@thepetersongroup.example.</p>',
  });
}

const LOGO_SAMPLE_AGENTS = {
  wide: { logo_width: 800, logo_height: 200, logo_url: 'https://cdn.example.com/logos/wide.png' },
  square: { logo_width: 400, logo_height: 400, logo_url: 'https://cdn.example.com/logos/square.png' },
  tall: { logo_width: 200, logo_height: 400, logo_url: 'https://cdn.example.com/logos/tall.png' },
};

function sampleBrokerageLogoEmail(kind) {
  const sample = LOGO_SAMPLE_AGENTS[kind];
  if (!sample) throw new Error(`Unknown logo sample: ${kind}`);
  const agent = {
    first_name: 'Charlie',
    last_name: 'Peterson',
    brokerage: 'Keller Williams Heritage',
    team_name: 'The Peterson Group',
    phone: '(407) 555-0100',
    email: 'charlie@example.com',
    headshot_url: 'https://cdn.example.com/headshot.png',
    brand_color: '#b91c1c',
  };
  Object.assign(agent, sample);
  const body = `
    <h2 style="margin:0 0 12px;color:#333333;font-size:24px;font-weight:500;">Your home insurance renews</h2>
    <p style="margin:0 0 12px;font-size:20px;font-weight:600;">2026-11-27</p>
    <p style="margin:0 0 16px;">This is 42 days out. A good time to shop or review the policy and save the declarations page with your records.</p>
    <p style="margin:0;"><a href="https://app.pacarion.com/" style="display:inline-block;padding:12px 24px;background-color:#0f766e;color:#ffffff;text-decoration:none;border-radius:6px;">Open your records</a></p>
  `;
  return notificationShell({
    title: 'Your home insurance renews',
    bodyHtml: body,
    agent,
    footerHtml: '<p style="margin:0;color:#667085;font-size:12px;line-height:1.6;text-align:center;">© 2026 Pacarion. All rights reserved.<br>This email was sent to jordan@example.com.</p>',
  });
}

function sampleSponsorlessEmail() {
  const body = `
    <h2 style="margin:0 0 12px;color:#333333;font-size:24px;font-weight:500;">Your home list</h2>
    <p style="margin:0 0 12px;">Hi Jordan,</p>
    <p style="margin:0 0 12px;">Here is this month's home list. The next check is due 10/20/2026.</p>
    <p style="margin:0;">Questions? Email support@pacarion.com.</p>
  `;
  return homeownerNotificationShell({
    title: 'Your home list',
    agent: null,
    sentTo: 'delivered+sponsorless@resend.dev',
    year: 2026,
    bodyHtml: body,
  });
}

function sampleChecklistEmail(agent) {
  const name = escapeHtml(agentDisplayName(agent) || 'your sponsor');
  const body = `
    <h2 style="margin:0 0 16px;color:#333333;font-size:24px;font-weight:500;">New Project Assigned</h2>
    <p style="margin:0 0 16px;">Hi Jordan,</p>
    <p style="margin:0 0 16px;">${name} has created a new project checklist for you to help prepare your home. Log in to your Pacarion dashboard to view and track your progress.</p>
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="border-collapse:collapse;margin:16px 0;background-color:#59c4c4;border-radius:8px;">
      <tr><td style="padding:20px;">
        <h3 style="margin:0 0 8px;color:#ffffff;font-size:20px;font-weight:500;">Pre-Listing Preparation</h3>
        <p style="margin:0;color:#ffffff;font-size:15px;">12 tasks</p>
      </td></tr>
    </table>
    <p style="margin:16px 0;"><a href="https://app.pacarion.com/?login=homeowner&section=projects" style="display:inline-block;padding:12px 24px;background-color:#0f766e;color:#ffffff;text-decoration:none;border-radius:6px;">View Project Dashboard</a></p>
    <p style="margin:0;color:#666666;font-size:14px;">Questions about this project? Contact ${name} or reach out to Pacarion support at support@pacarion.com.</p>
  `;
  return notificationShell({
    title: 'New Project - Pre-Listing Preparation',
    bodyHtml: body,
    agent,
    personLabel: 'Sponsor',
    footerHtml: '<p style="margin:0;color:#667085;font-size:12px;line-height:1.6;text-align:center;">© 2026 Pacarion. All rights reserved.<br>This email was sent to jordan@example.com.</p>',
  });
}

module.exports = {
  PACARION_LOGO_URL,
  PACARION_EMAIL_HEADER_BG,
  PACARION_LOGO_WIDTH,
  PACARION_LOGO_HEIGHT,
  PACARION_POSTAL_ADDRESS,
  PACARION_SUPPORT_EMAIL,
  LOGO_MAX_WIDTH,
  LOGO_MAX_HEIGHT,
  fitLogoDimensions,
  escapeHtml,
  agentDisplayName,
  logoHeaderHtml,
  agentBrandingHtml,
  hasAgentBranding,
  pacarionFooterHtml,
  notificationShell,
  homeownerNotificationShell,
  homeownerBrandingAgentId,
  completionNotice,
  sampleChecklistEmail,
  sampleCompletionEmail,
  sampleBrokerageLogoEmail,
  sampleSponsorlessEmail,
};
